/**
 * LeadStatusManagerDialog — Iter Feb 2026 · R68.
 *
 * Workspace-level configuration of lead statuses. Reachable from the
 * Leads screen (gear icon next to the status filter). Admins can:
 *   • Add / rename / recolour / archive statuses
 *   • Reorder via up/down arrows
 *   • Tag terminal-won or terminal-lost (auto-stamps closed_at)
 *   • Hard-delete an unused status (auto-migrate to another status if
 *     leads still reference it)
 *
 * Renaming the **label** flows through instantly to every existing
 * lead — leads carry the stable ``key`` slug, and labels come from
 * this collection at read time. Changing the key is not allowed
 * post-create (that guarantee is enforced server-side).
 */
import { useCallback, useEffect, useState } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  ArrowUp, ArrowDown, Loader2, Plus, Trash2, Undo2,
  Archive, ArchiveRestore, Trophy, XCircle, Check,
} from "lucide-react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";

const COLOR_SWATCHES = [
  "#94a3b8", "#38bdf8", "#818cf8", "#a78bfa", "#f472b6",
  "#f59e0b", "#10b981", "#ef4444", "#0ea5e9", "#e879f9",
];

export default function LeadStatusManagerDialog({ open, onOpenChange, wsId, onChange }) {
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [newLabel, setNewLabel] = useState("");
  const [newColor, setNewColor] = useState("#94a3b8");
  // For confirm-migrate flow when trying to delete an in-use status
  const [migratePrompt, setMigratePrompt] = useState(null);   // { row, target }

  const load = useCallback(async () => {
    if (!open || !wsId) return;
    setRows(null);
    try {
      const { data } = await api.get(`/workspaces/${wsId}/lead-statuses`, {
        params: { include_archived: true },
      });
      setRows(data.rows || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Load failed")); setRows([]); }
  }, [open, wsId]);

  useEffect(() => { load(); }, [load]);

  const notifyChange = () => onChange?.();

  const addStatus = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/lead-statuses`,
        { label: newLabel.trim(), color: newColor });
      setNewLabel(""); setNewColor("#94a3b8");
      toast.success("Status added");
      await load(); notifyChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Add failed")); }
    finally { setBusy(false); }
  };

  const patch = async (sid, body) => {
    setBusy(true);
    try {
      await api.patch(`/workspaces/${wsId}/lead-statuses/${sid}`, body);
      await load(); notifyChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
    finally { setBusy(false); }
  };

  const reorder = async (idx, dir) => {
    if (!rows) return;
    const target = idx + dir;
    if (target < 0 || target >= rows.length) return;
    const clone = [...rows];
    [clone[idx], clone[target]] = [clone[target], clone[idx]];
    setRows(clone);
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/lead-statuses/reorder`,
        { order: clone.map(r => r.id) });
      await load(); notifyChange();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Reorder failed"));
      await load();
    } finally { setBusy(false); }
  };

  const deleteRow = async (row) => {
    // If in-use, open the migrate-to picker instead of hard 409
    if (row.lead_count > 0) {
      setMigratePrompt({ row, target: "" });
      return;
    }
    if (!window.confirm(`Delete status "${row.label}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/workspaces/${wsId}/lead-statuses/${row.id}`);
      toast.success("Status deleted");
      await load(); notifyChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };

  const confirmMigrateAndDelete = async () => {
    if (!migratePrompt?.target) return;
    setBusy(true);
    try {
      await api.delete(
        `/workspaces/${wsId}/lead-statuses/${migratePrompt.row.id}`,
        { params: { migrate_to: migratePrompt.target } },
      );
      toast.success(`Deleted · ${migratePrompt.row.lead_count} leads migrated to ${migratePrompt.target}`);
      setMigratePrompt(null);
      await load(); notifyChange();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
    finally { setBusy(false); }
  };

  const active = rows?.filter(r => !r.archived_at) || [];
  const archived = rows?.filter(r => r.archived_at) || [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-hidden flex flex-col rounded-none"
                       data-testid="lead-status-manager">
        <DialogHeader className="shrink-0">
          <DialogTitle className="text-base">Manage Lead statuses</DialogTitle>
          <p className="text-xs text-muted-foreground">
            Statuses apply to <b>this workspace</b>. Renaming a label flows through to every
            existing lead using it — the underlying key stays stable.
          </p>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto space-y-3 pr-1">
          {rows === null && (
            <div className="p-8 text-center text-xs text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading…
            </div>
          )}
          {rows && (
            <>
              {/* Active list */}
              <div className="border bg-card">
                <div className="px-3 py-2 border-b text-[10px] uppercase tracking-widest text-muted-foreground">
                  Active · {active.length}
                </div>
                <table className="w-full text-xs">
                  <tbody>
                    {active.map((row, idx) => (
                      <StatusRow
                        key={row.id}
                        row={row}
                        idx={idx}
                        canMoveUp={idx > 0}
                        canMoveDown={idx < active.length - 1}
                        busy={busy}
                        onReorder={reorder}
                        onPatch={patch}
                        onDelete={deleteRow}
                      />
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Add-new row */}
              <div className="border bg-card p-3 space-y-2" data-testid="lead-status-add-row">
                <Label className="text-[10px] uppercase tracking-widest text-muted-foreground">
                  Add a new status
                </Label>
                <div className="flex gap-1.5 items-center flex-wrap">
                  <Input
                    value={newLabel}
                    onChange={e => setNewLabel(e.target.value)}
                    onKeyDown={e => { if (e.key === "Enter") addStatus(); }}
                    placeholder="e.g. Nurture, Contract sent, On-hold…"
                    className="h-8 rounded-none text-xs flex-1 min-w-40"
                    data-testid="lead-status-new-label"
                  />
                  <ColorPicker value={newColor} onChange={setNewColor} testid="lead-status-new-color" />
                  <Button size="sm" onClick={addStatus} disabled={busy || !newLabel.trim()}
                             className="rounded-none h-8" data-testid="lead-status-add-btn">
                    <Plus className="w-3 h-3 mr-1" /> Add
                  </Button>
                </div>
              </div>

              {/* Archived section */}
              {archived.length > 0 && (
                <div className="border bg-card">
                  <div className="px-3 py-2 border-b text-[10px] uppercase tracking-widest text-muted-foreground">
                    Archived · {archived.length}
                  </div>
                  <table className="w-full text-xs">
                    <tbody>
                      {archived.map(row => (
                        <tr key={row.id} className="border-t opacity-60">
                          <td className="p-2 w-6">
                            <span className="inline-block w-3 h-3" style={{ background: row.color }} />
                          </td>
                          <td className="p-2 truncate">{row.label}</td>
                          <td className="p-2 text-[10px] text-muted-foreground truncate">{row.key}</td>
                          <td className="p-2 text-[10px] text-right w-16">{row.lead_count}</td>
                          <td className="p-2 text-right w-32">
                            <button
                              onClick={() => patch(row.id, { archived: false })}
                              disabled={busy}
                              className="text-[11px] text-primary hover:underline inline-flex items-center gap-1"
                              data-testid={`lead-status-restore-${row.id}`}
                            >
                              <ArchiveRestore className="w-3 h-3" /> Restore
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>

        <DialogFooter className="shrink-0 pt-2 border-t">
          <Button variant="outline" size="sm" onClick={() => onOpenChange?.(false)}
                     className="rounded-none" data-testid="lead-status-close">
            Done
          </Button>
        </DialogFooter>
      </DialogContent>

      {/* Migrate-then-delete confirmation */}
      {migratePrompt && (
        <Dialog open={!!migratePrompt} onOpenChange={(o) => !o && setMigratePrompt(null)}>
          <DialogContent className="max-w-md rounded-none" data-testid="lead-status-migrate-dialog">
            <DialogHeader>
              <DialogTitle>Migrate {migratePrompt.row.lead_count} leads first</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">
              <b>{migratePrompt.row.label}</b> is used by {migratePrompt.row.lead_count} lead
              {migratePrompt.row.lead_count === 1 ? "" : "s"}. Pick a status to move them to
              before deletion — no lead ever ends up orphaned.
            </p>
            <select
              value={migratePrompt.target}
              onChange={(e) => setMigratePrompt({ ...migratePrompt, target: e.target.value })}
              className="h-9 border w-full text-xs rounded-none px-2"
              data-testid="lead-status-migrate-target"
            >
              <option value="">— Pick a target status —</option>
              {(rows || []).filter(r => r.id !== migratePrompt.row.id && !r.archived_at).map(r => (
                <option key={r.id} value={r.key}>{r.label} ({r.key})</option>
              ))}
            </select>
            <DialogFooter className="pt-2">
              <Button variant="outline" size="sm" className="rounded-none"
                         onClick={() => setMigratePrompt(null)}>Cancel</Button>
              <Button size="sm" className="rounded-none"
                         disabled={!migratePrompt.target || busy}
                         onClick={confirmMigrateAndDelete}
                         data-testid="lead-status-migrate-confirm">
                Migrate & delete
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  );
}

function StatusRow({ row, idx, canMoveUp, canMoveDown, busy, onReorder, onPatch, onDelete }) {
  const [editing, setEditing] = useState(false);
  const [label, setLabel] = useState(row.label);
  const [color, setColor] = useState(row.color);

  const commit = () => {
    const patch = {};
    if (label.trim() && label !== row.label) patch.label = label.trim();
    if (color && color !== row.color) patch.color = color;
    if (Object.keys(patch).length) onPatch(row.id, patch);
    setEditing(false);
  };

  const toggleTermWin = () => onPatch(row.id, {
    is_terminal_won: !row.is_terminal_won,
    is_terminal_lost: false,
  });
  const toggleTermLose = () => onPatch(row.id, {
    is_terminal_lost: !row.is_terminal_lost,
    is_terminal_won: false,
  });

  return (
    <tr className="border-t hover:bg-muted/20" data-testid={`lead-status-row-${row.id}`}>
      <td className="p-2 w-14 whitespace-nowrap">
        <button onClick={() => onReorder(idx, -1)} disabled={!canMoveUp || busy}
                  className="p-0.5 disabled:opacity-30" title="Move up"
                  data-testid={`lead-status-up-${row.id}`}>
          <ArrowUp className="w-3.5 h-3.5" />
        </button>
        <button onClick={() => onReorder(idx, +1)} disabled={!canMoveDown || busy}
                  className="p-0.5 disabled:opacity-30" title="Move down"
                  data-testid={`lead-status-down-${row.id}`}>
          <ArrowDown className="w-3.5 h-3.5" />
        </button>
      </td>
      <td className="p-2 w-6">
        {editing ? (
          <ColorPicker value={color} onChange={setColor} testid={`lead-status-color-${row.id}`} />
        ) : (
          <span className="inline-block w-3 h-3" style={{ background: row.color }} />
        )}
      </td>
      <td className="p-2">
        {editing ? (
          <Input value={label} onChange={e => setLabel(e.target.value)}
                   onKeyDown={e => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setLabel(row.label); setEditing(false); } }}
                   className="h-7 rounded-none text-xs" autoFocus
                   data-testid={`lead-status-label-input-${row.id}`} />
        ) : (
          <button onClick={() => setEditing(true)} className="text-left hover:underline"
                    data-testid={`lead-status-label-${row.id}`}>
            {row.label}
            {row.is_default && <span className="ml-1 text-[9px] uppercase text-muted-foreground">· default</span>}
          </button>
        )}
      </td>
      <td className="p-2 text-[10px] text-muted-foreground font-mono truncate max-w-24" title={`key: ${row.key} (immutable)`}>{row.key}</td>
      <td className="p-2 text-[10px] text-right text-muted-foreground w-14 tabular-nums">{row.lead_count}</td>
      <td className="p-2 text-right w-44 whitespace-nowrap">
        {editing ? (
          <>
            <button onClick={commit} disabled={busy}
                      className="p-1 text-emerald-600 hover:bg-emerald-50" title="Save"
                      data-testid={`lead-status-save-${row.id}`}>
              <Check className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => { setLabel(row.label); setColor(row.color); setEditing(false); }}
                      className="p-1 hover:bg-muted" title="Cancel">
              <Undo2 className="w-3.5 h-3.5" />
            </button>
          </>
        ) : (
          <>
            <button onClick={toggleTermWin} disabled={busy}
                      className={`p-1 ${row.is_terminal_won ? "text-emerald-600 bg-emerald-50" : "hover:bg-muted text-muted-foreground"}`}
                      title={row.is_terminal_won ? "Terminal WIN — auto-stamps closed_at" : "Mark as terminal WIN"}
                      data-testid={`lead-status-tw-${row.id}`}>
              <Trophy className="w-3.5 h-3.5" />
            </button>
            <button onClick={toggleTermLose} disabled={busy}
                      className={`p-1 ${row.is_terminal_lost ? "text-rose-600 bg-rose-50" : "hover:bg-muted text-muted-foreground"}`}
                      title={row.is_terminal_lost ? "Terminal LOST — auto-stamps closed_at" : "Mark as terminal LOST"}
                      data-testid={`lead-status-tl-${row.id}`}>
              <XCircle className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onPatch(row.id, { archived: true })} disabled={busy}
                      className="p-1 hover:bg-muted text-muted-foreground"
                      title="Archive (keeps historic data)"
                      data-testid={`lead-status-archive-${row.id}`}>
              <Archive className="w-3.5 h-3.5" />
            </button>
            <button onClick={() => onDelete(row)} disabled={busy}
                      className="p-1 hover:bg-rose-50 text-rose-600"
                      title={row.lead_count > 0 ? "Delete (needs migration)" : "Delete"}
                      data-testid={`lead-status-delete-${row.id}`}>
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

function ColorPicker({ value, onChange, testid }) {
  return (
    <div className="inline-flex gap-0.5 flex-wrap items-center" data-testid={testid}>
      {COLOR_SWATCHES.map(c => (
        <button
          key={c}
          type="button"
          onClick={() => onChange(c)}
          className={`w-4 h-4 border ${c === value ? "ring-2 ring-offset-1 ring-primary" : ""}`}
          style={{ background: c }}
          title={c}
        />
      ))}
    </div>
  );
}
